/**
 * Ports editor component.
 *
 * Provides a compact UI for editing a list of ports to expose on a sandbox.
 *
 * @module components/ui/PortsEditor
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus, Settings, X, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Input } from './Input';
import { Select } from './Select';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Modal } from './Modal';
import { TagInput } from './TagInput';
import { IpAccessControlEditor } from './IpAccessControlEditor';
import type {
  PortProtocol,
  PortIpAccessControl,
  PortCorsConfig,
  IpAccessControlAction,
  PortIpAccessControlRule,
} from '@/types';

export type PortAuthMode = 'entraId' | 'github' | 'publicGithub' | 'none';

export interface PortAuthConfig {
  mode: PortAuthMode;
  emails: string[];
  emailSuffixes: string[];
  usernames: string[]; // GitHub only
  usernameSuffixes: string[]; // GitHub only
  objectIds: string[]; // Entra ID only
  tenantIds: string[]; // Entra ID only
}

export interface PortEntry {
  port: number;
  name?: string;
  protocol: PortProtocol;
  auth?: PortAuthConfig;
  ipAccessControl?: PortIpAccessControl;
  cors?: PortCorsConfig;
}

export interface PortsEditorProps {
  /** Current ports value */
  value: PortEntry[];
  /** Called when the ports list changes */
  onChange: (next: PortEntry[]) => void;
  /** Optional heading label */
  label?: string;
  /** Optional helper/description text */
  helperText?: string;
  /** Disable editing */
  disabled?: boolean;
  /** When true, auth is required on all ports (no anonymous option). Used for personal connectors. */
  requireAuth?: boolean;
  /** The logged-in user's email. When requireAuth is true, this is prefilled as the only allowed identity. */
  userEmail?: string;
}

const defaultAuthConfig: PortAuthConfig = {
  mode: 'entraId',
  emails: [],
  emailSuffixes: [],
  usernames: [],
  usernameSuffixes: [],
  objectIds: [],
  tenantIds: [],
};

/**
 * A ports editor for adding/removing ports during sandbox creation.
 */
export function PortsEditor({
  value,
  onChange,
  label = 'Ports (Optional)',
  helperText,
  disabled = false,
  requireAuth = false,
  userEmail,
}: PortsEditorProps) {
  const [draftPort, setDraftPort] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftProtocol, setDraftProtocol] = useState<PortProtocol>('Http');
  const [draftAuth, setDraftAuth] = useState<PortAuthConfig>(() =>
    requireAuth && userEmail
      ? { ...defaultAuthConfig, mode: 'entraId', emails: [userEmail] }
      : { ...defaultAuthConfig }
  );

  // Update draft auth when requireAuth changes (e.g., user adds/removes a personal connector)
  useEffect(() => {
    if (requireAuth && userEmail) {
      setDraftAuth({ ...defaultAuthConfig, mode: 'entraId', emails: [userEmail] });
    }
  }, [requireAuth, userEmail]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Port config modal state
  const [configPort, setConfigPort] = useState<number | null>(null);
  const [cfgAuthMode, setCfgAuthMode] = useState<PortAuthMode>('entraId');
  const [cfgEmails, setCfgEmails] = useState<string[]>([]);
  const [cfgEmailSuffixes, setCfgEmailSuffixes] = useState<string[]>([]);
  const [cfgUsernames, setCfgUsernames] = useState<string[]>([]);
  const [cfgUsernameSuffixes, setCfgUsernameSuffixes] = useState<string[]>([]);
  const [cfgIpDefaultAction, setCfgIpDefaultAction] = useState<IpAccessControlAction>('Allow');
  const [cfgIpRules, setCfgIpRules] = useState<PortIpAccessControlRule[]>([]);
  const [showIpSection, setShowIpSection] = useState(false);
  const [cfgCorsAllowOrigins, setCfgCorsAllowOrigins] = useState<string[]>([]);
  const [cfgCorsAllowMethods, setCfgCorsAllowMethods] = useState<string[]>([]);
  const [cfgCorsAllowHeaders, setCfgCorsAllowHeaders] = useState<string[]>([]);
  const [cfgCorsAllowCredentials, setCfgCorsAllowCredentials] = useState(false);
  const [cfgCorsMaxAge, setCfgCorsMaxAge] = useState('');
  const [showCorsSection, setShowCorsSection] = useState(false);

  const canAdd = useMemo(() => {
    const portNum = parseInt(draftPort.trim(), 10);
    return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
  }, [draftPort]);

  const resetDraft = () => {
    setDraftPort('');
    setDraftName('');
    setDraftProtocol('Http');
    setDraftAuth({ ...defaultAuthConfig });
    setShowAdvanced(false);
  };

  const addPort = () => {
    if (disabled) return;

    const portNum = parseInt(draftPort.trim(), 10);
    const name = draftName.trim() || undefined;

    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      setError('Port must be a number between 1 and 65535');
      return;
    }

    // Check for duplicate port
    if (value.some((p) => p.port === portNum)) {
      setError(`Port ${portNum} is already added`);
      return;
    }

    // Validate name format if provided
    if (name && !/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(name)) {
      setError('Name must be alphanumeric with optional hyphens (cannot start/end with hyphen)');
      return;
    }

    // Check for duplicate name
    if (name && value.some((p) => p.name === name)) {
      setError(`Port name "${name}" is already used`);
      return;
    }

    setError(null);
    onChange([...value, { port: portNum, name, protocol: draftProtocol, auth: { ...draftAuth } }]);
    resetDraft();
  };

  const removePort = (port: number) => {
    if (disabled) return;
    onChange(value.filter((p) => p.port !== port));
  };

  const getAuthLabel = (auth?: PortAuthConfig) => {
    if (!auth || auth.mode === 'none') return 'Public';
    if (auth.mode === 'entraId') return 'Entra ID';
    if (auth.mode === 'publicGithub') return 'GitHub (public)';
    return 'GitHub';
  };

  const getAuthBadgeClass = (auth?: PortAuthConfig) => {
    if (!auth || auth.mode === 'none')
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    if (auth.mode === 'entraId')
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  const openConfigModal = (entry: PortEntry) => {
    setConfigPort(entry.port);
    setCfgAuthMode(entry.auth?.mode ?? 'entraId');
    setCfgEmails(entry.auth?.emails ?? []);
    setCfgEmailSuffixes(entry.auth?.emailSuffixes ?? []);
    setCfgUsernames(entry.auth?.usernames ?? []);
    setCfgUsernameSuffixes(entry.auth?.usernameSuffixes ?? []);
    setCfgIpDefaultAction(entry.ipAccessControl?.defaultAction ?? 'Allow');
    setCfgIpRules(entry.ipAccessControl?.rules ?? []);
    setShowIpSection(!!entry.ipAccessControl);
    setCfgCorsAllowOrigins(entry.cors?.allowOrigins ?? []);
    setCfgCorsAllowMethods(entry.cors?.allowMethods ?? []);
    setCfgCorsAllowHeaders(entry.cors?.allowHeaders ?? []);
    setCfgCorsAllowCredentials(!!entry.cors?.allowCredentials);
    setCfgCorsMaxAge(entry.cors?.maxAge?.toString() ?? '');
    setShowCorsSection(!!entry.cors);
  };

  const closeConfigModal = () => setConfigPort(null);

  const saveConfig = () => {
    if (configPort === null) return;

    const auth: PortAuthConfig =
      cfgAuthMode === 'none'
        ? { ...defaultAuthConfig, mode: 'none' }
        : cfgAuthMode === 'github'
          ? {
              ...defaultAuthConfig,
              mode: 'github',
              emails: cfgEmails,
              emailSuffixes: cfgEmailSuffixes,
              usernames: cfgUsernames,
              usernameSuffixes: cfgUsernameSuffixes,
            }
          : cfgAuthMode === 'publicGithub'
            ? {
                ...defaultAuthConfig,
                mode: 'publicGithub',
                emails: cfgEmails,
                emailSuffixes: cfgEmailSuffixes,
                usernames: cfgUsernames,
                usernameSuffixes: cfgUsernameSuffixes,
              }
            : { ...defaultAuthConfig, mode: 'entraId' };

    const ipAccessControl: PortIpAccessControl | undefined =
      cfgIpRules.length > 0 || cfgIpDefaultAction !== 'Allow'
        ? { defaultAction: cfgIpDefaultAction, rules: cfgIpRules }
        : undefined;

    const parsedMaxAge = cfgCorsMaxAge.trim() ? parseInt(cfgCorsMaxAge.trim(), 10) : undefined;
    const cors: PortCorsConfig | undefined =
      cfgCorsAllowOrigins.length > 0 ||
      cfgCorsAllowMethods.length > 0 ||
      cfgCorsAllowHeaders.length > 0 ||
      cfgCorsAllowCredentials ||
      parsedMaxAge !== undefined
        ? {
            allowOrigins: cfgCorsAllowOrigins,
            allowMethods: cfgCorsAllowMethods,
            allowHeaders: cfgCorsAllowHeaders,
            allowCredentials: cfgCorsAllowCredentials || undefined,
            maxAge: Number.isFinite(parsedMaxAge) ? parsedMaxAge : undefined,
          }
        : undefined;

    onChange(value.map((p) => (p.port === configPort ? { ...p, auth, ipAccessControl, cors } : p)));
    closeConfigModal();
  };

  const configEntry = value.find((p) => p.port === configPort);
  const hasConfig = (entry: PortEntry) => !!entry.ipAccessControl || !!entry.cors;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
        {helperText && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((entry) => (
            <span
              key={entry.port}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200"
            >
              {entry.port}
              {entry.name && (
                <span className="text-blue-600 dark:text-blue-400">:{entry.name}</span>
              )}
              <span
                className={`ml-1 px-1 py-0.5 text-[10px] rounded ${getAuthBadgeClass(entry.auth)}`}
              >
                {getAuthLabel(entry.auth)}
              </span>
              {entry.protocol === 'Http2' && (
                <span className="px-1 py-0.5 text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                  HTTP/2
                </span>
              )}
              {hasConfig(entry) && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                  configured
                </span>
              )}
              {!requireAuth && (
                <button
                  type="button"
                  onClick={() => openConfigModal(entry)}
                  className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                  disabled={disabled}
                  aria-label={`Configure port ${entry.port}`}
                >
                  <Settings className="h-3 w-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => removePort(entry.port)}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                disabled={disabled}
                aria-label={`Remove port ${entry.port}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
        <div className="flex gap-2 items-end">
          <div className="w-20">
            <Input
              label="Port"
              placeholder="8080"
              value={draftPort}
              onChange={(e) => {
                setDraftPort(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (canAdd) addPort();
                }
              }}
              disabled={disabled}
              type="number"
              min={1}
              max={65535}
            />
          </div>
          {!requireAuth && (
            <div className="flex-1">
              <Input
                label="Name"
                placeholder="Optional"
                value={draftName}
                onChange={(e) => {
                  setDraftName(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canAdd) addPort();
                  }
                }}
                disabled={disabled}
              />
            </div>
          )}
          {!requireAuth && (
            <div className="w-48">
              <Select
                label="Authentication"
                options={[
                  { value: 'entraId', label: 'Microsoft Entra ID' },
                  { value: 'github', label: 'GitHub (EMU)' },
                  { value: 'publicGithub', label: 'GitHub (public)' },
                  { value: 'none', label: 'No authentication' },
                ]}
                value={draftAuth.mode}
                onChange={(e) => {
                  const mode = e.target.value as PortAuthMode;
                  setDraftAuth((prev) => ({
                    ...prev,
                    mode,
                    usernames: [],
                    usernameSuffixes: [],
                    objectIds: [],
                    tenantIds: [],
                  }));
                }}
                disabled={disabled}
              />
            </div>
          )}
        </div>

        {requireAuth && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Port access is restricted to <strong>{userEmail}</strong> because this sandbox uses
              personal connectors.
            </p>
          </div>
        )}

        {draftAuth.mode === 'none' && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              This port will be accessible to anyone without authentication.
            </p>
          </div>
        )}

        {draftAuth.mode !== 'none' && !requireAuth && (
          <div className="space-y-2">
            <TagInput
              label="Allowed Emails"
              value={draftAuth.emails}
              onChange={(emails) => setDraftAuth((prev) => ({ ...prev, emails }))}
              placeholder="user@example.com"
              helperText="Leave empty to allow any authenticated user"
              validate={(v) => {
                const pattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                return pattern.test(v) ? null : 'Invalid email';
              }}
              disabled={disabled}
            />
            <TagInput
              label="Email Suffixes"
              value={draftAuth.emailSuffixes}
              onChange={(emailSuffixes) => setDraftAuth((prev) => ({ ...prev, emailSuffixes }))}
              placeholder="@company.com"
              validate={(v) => {
                const pattern = /^@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                return pattern.test(v) ? null : 'Must start with @';
              }}
              disabled={disabled}
            />
            {(draftAuth.mode === 'github' || draftAuth.mode === 'publicGithub') && (
              <>
                <TagInput
                  label="Usernames"
                  value={draftAuth.usernames}
                  onChange={(usernames) => setDraftAuth((prev) => ({ ...prev, usernames }))}
                  placeholder="octocat"
                  validate={(v) => {
                    const pattern = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,38}$/;
                    return pattern.test(v) ? null : 'Invalid username';
                  }}
                  disabled={disabled}
                />
                <TagInput
                  label="Username Suffixes"
                  value={draftAuth.usernameSuffixes}
                  onChange={(usernameSuffixes) =>
                    setDraftAuth((prev) => ({ ...prev, usernameSuffixes }))
                  }
                  placeholder="_microsoft"
                  helperText="For EMU accounts"
                  validate={(v) => {
                    const pattern = /^_[a-zA-Z0-9_-]+$/;
                    return pattern.test(v) ? null : 'Must start with _';
                  }}
                  disabled={disabled}
                />
              </>
            )}
            {draftAuth.mode === 'entraId' && (
              <>
                <TagInput
                  label="Object IDs"
                  value={draftAuth.objectIds}
                  onChange={(objectIds) => setDraftAuth((prev) => ({ ...prev, objectIds }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  helperText="User or group object IDs"
                  validate={(v) => {
                    const pattern =
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                    return pattern.test(v) ? null : 'Invalid GUID';
                  }}
                  disabled={disabled}
                />
                <TagInput
                  label="Tenant IDs"
                  value={draftAuth.tenantIds}
                  onChange={(tenantIds) => setDraftAuth((prev) => ({ ...prev, tenantIds }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  validate={(v) => {
                    const pattern =
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                    return pattern.test(v) ? null : 'Invalid GUID';
                  }}
                  disabled={disabled}
                />
              </>
            )}
          </div>
        )}

        {!requireAuth && (
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Advanced options
          </button>
        )}

        {showAdvanced && !requireAuth && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <Select
              label="Protocol"
              options={[
                { value: 'Http', label: 'HTTP/1.1' },
                { value: 'Http2', label: 'HTTP/2 (gRPC)' },
              ]}
              value={draftProtocol}
              onChange={(e) => setDraftProtocol(e.target.value as PortProtocol)}
              disabled={disabled}
            />
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={addPort}
            disabled={disabled || !canAdd}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Port
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-danger-500 dark:text-danger-400">{error}</p>}

      {/* Port Configuration Modal */}
      <Modal
        isOpen={configPort !== null}
        onClose={closeConfigModal}
        title={`Configure Port ${configPort}`}
        description="Set authentication, IP access control, and CORS for this port."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeConfigModal}>
              Cancel
            </Button>
            <Button onClick={saveConfig}>Save</Button>
          </>
        }
      >
        {configEntry && (
          <div className="space-y-4">
            {requireAuth && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-400">
                Authentication is required because this sandbox uses personal connectors that access
                your private data.
              </div>
            )}
            <Select
              label="Authentication"
              options={[
                { value: 'entraId', label: 'Microsoft Entra ID' },
                { value: 'github', label: 'GitHub (EMU)' },
                { value: 'publicGithub', label: 'GitHub (public)' },
                { value: 'none', label: 'No authentication (public)' },
              ]}
              value={cfgAuthMode}
              onChange={(e) => {
                const mode = e.target.value as PortAuthMode;
                setCfgAuthMode(mode);
                setCfgEmails([]);
                setCfgEmailSuffixes([]);
                setCfgUsernames([]);
                setCfgUsernameSuffixes([]);
              }}
              disabled={requireAuth}
            />
            {cfgAuthMode === 'none' && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  This port will be accessible to anyone without authentication.
                </p>
              </div>
            )}
            {(cfgAuthMode === 'github' || cfgAuthMode === 'publicGithub') && (
              <>
                <TagInput
                  label="Allowed Emails (Optional)"
                  value={cfgEmails}
                  onChange={setCfgEmails}
                  placeholder="user@example.com"
                  helperText="Exact email addresses that can access this port"
                  validate={(v) => {
                    const pat = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                    return pat.test(v) ? null : 'Enter a valid email address';
                  }}
                />
                <TagInput
                  label="Email Suffixes (Optional)"
                  value={cfgEmailSuffixes}
                  onChange={setCfgEmailSuffixes}
                  placeholder="@company.com"
                  helperText="Allow emails ending with this suffix"
                  validate={(v) => {
                    const pat = /^@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                    return pat.test(v) ? null : 'Must start with @ (e.g., @company.com)';
                  }}
                />
                <TagInput
                  label="Allowed Usernames (Optional)"
                  value={cfgUsernames}
                  onChange={setCfgUsernames}
                  placeholder="octocat"
                  helperText="Exact GitHub usernames that can access this port"
                  validate={(v) => {
                    const pat = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,38}$/;
                    return pat.test(v) ? null : 'Enter a valid GitHub username';
                  }}
                />
                <TagInput
                  label="Username Suffixes (Optional)"
                  value={cfgUsernameSuffixes}
                  onChange={setCfgUsernameSuffixes}
                  placeholder="_microsoft"
                  helperText="Allow EMU usernames ending with this suffix"
                  validate={(v) => {
                    const pat = /^_[a-zA-Z0-9_-]+$/;
                    return pat.test(v) ? null : 'Must start with _ (e.g., _microsoft)';
                  }}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Leave all fields empty to allow any authenticated GitHub user.
                </p>
              </>
            )}

            {/* IP Access Control (collapsible) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <button
                type="button"
                className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
                onClick={() => setShowIpSection(!showIpSection)}
              >
                <span>IP Access Control (Optional)</span>
                <span className="text-xs text-gray-400">{showIpSection ? '▲' : '▼'}</span>
              </button>
              {showIpSection && (
                <div className="px-3 pb-3">
                  <IpAccessControlEditor
                    defaultAction={cfgIpDefaultAction}
                    onDefaultActionChange={setCfgIpDefaultAction}
                    rules={cfgIpRules}
                    onRulesChange={setCfgIpRules}
                  />
                </div>
              )}
            </div>

            {/* CORS (collapsible) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <button
                type="button"
                className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
                onClick={() => setShowCorsSection(!showCorsSection)}
              >
                <span>CORS (Optional)</span>
                <span className="text-xs text-gray-400">{showCorsSection ? '▲' : '▼'}</span>
              </button>
              {showCorsSection && (
                <div className="px-3 pb-3 space-y-3">
                  <TagInput
                    label="Allow Origins"
                    value={cfgCorsAllowOrigins}
                    onChange={setCfgCorsAllowOrigins}
                    placeholder="https://example.com"
                  />
                  <TagInput
                    label="Allow Methods"
                    value={cfgCorsAllowMethods}
                    onChange={setCfgCorsAllowMethods}
                    placeholder="GET"
                  />
                  <TagInput
                    label="Allow Headers"
                    value={cfgCorsAllowHeaders}
                    onChange={setCfgCorsAllowHeaders}
                    placeholder="Content-Type"
                  />
                  <Checkbox
                    label="Allow credentials"
                    checked={cfgCorsAllowCredentials}
                    onChange={(e) => setCfgCorsAllowCredentials(e.target.checked)}
                  />
                  <Input
                    label="Max age (seconds)"
                    type="number"
                    min={0}
                    placeholder="86400"
                    value={cfgCorsMaxAge}
                    onChange={(e) => setCfgCorsMaxAge(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
